/**
 * OTP Service
 * Handles OTP verification API calls for package acceptance
 */

import { api } from '../lib/axios';

export interface OTPGenerationResult {
  success: boolean;
  otpId?: string;
  expiryTime?: string;
  deliveryMethod?: 'sms' | 'email';
  deliveryStatus?: 'sent' | 'failed';
  attemptsRemaining?: number;
  error?: string;
}

export interface OTPVerificationResult {
  success: boolean;
  valid?: boolean;
  expired?: boolean;
  attemptsRemaining?: number;
  packageActivated?: boolean;
  message?: string;
  error?: string;
}

export interface OTPStatus {
  exists: boolean;
  expired: boolean;
  verified: boolean;
  attemptsUsed: number;
  maxAttempts: number;
  expiryTime?: string;
  createdAt?: string;
}

class OTPService {
  /**
   * Generate OTP for package acceptance
   */
  async generateOTP(dealerId: string, packageAssignmentId: string): Promise<OTPGenerationResult> {
    try {
      const response = await api.post(`/dealers/${dealerId}/packages/${packageAssignmentId}/accept`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to generate OTP'
      };
    }
  }

  /**
   * Verify OTP and activate package
   */
  async verifyOTP(dealerId: string, packageAssignmentId: string, otp: string): Promise<OTPVerificationResult> {
    try {
      const response = await api.post(`/dealers/${dealerId}/packages/${packageAssignmentId}/verify-otp`, { otp });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to verify OTP'
      };
    }
  }

  /**
   * Resend OTP
   */
  async resendOTP(dealerId: string, packageAssignmentId: string): Promise<OTPGenerationResult> {
    try {
      const response = await api.post(`/dealers/${dealerId}/packages/${packageAssignmentId}/resend-otp`);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resend OTP'
      };
    }
  }
}

export const otpService = new OTPService();